import React, { useMemo, useState, useEffect } from 'react';
import {
  Database, Server, Radio, Cpu, RefreshCw, ShieldCheck, AlertTriangle, Activity, ArrowRight,
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useMarkdownContent } from '../utils/markdown';

// ============================================================
// 母機知識庫（MotherBase-KB）總覽 TAB —— 2026-09-28
// 形象化目標：一眼睇到「邊個接入 → 行邊條通道 → 到 server → 落到 SOULS → 回流返去」
// 數據來源：/src/content/knowhow/motherbase-kb.md 入面嘅 ```json 快照
// （Captain 改 md 就改圖，唔使改碼；讀唔到就用下面嘅 FALLBACK，版面永遠唔會開天窗）
// ============================================================

const CONTENT_PATH = '/src/content/knowhow/motherbase-kb.md';

interface DomainStat { name: string; count: number; desc: string }
interface TierStat { name: string; count: number; desc: string }

interface KbSnapshot {
  as_of: string;
  server: { version: string; tools: number; owner: string; http: string };
  rules: {
    total: number; active: number; draft: number; tombstones: number;
    collisions: number; unknown_categories: number; stale_tombstones: number;
    missing_frontmatter: number; missing_tags: number;
  };
  tiers: TierStat[];
  domains: DomainStat[];
}

const FALLBACK: KbSnapshot = {
  as_of: '—',
  server: { version: '2.2', tools: 10, owner: 'SYSTEM 排程', http: 'https://mcp.couponly.io/mcp' },
  rules: {
    total: 202, active: 194, draft: 7, tombstones: 0,
    collisions: 0, unknown_categories: 0, stale_tombstones: 0,
    missing_frontmatter: 0, missing_tags: 0,
  },
  tiers: [
    { name: 'core', count: 14, desc: 'L1 核心層（跨專案通用，開工必讀）' },
    { name: 'domain', count: 146, desc: 'L2 領域層（11 域，按需查閱）' },
    { name: 'case', count: 42, desc: 'L3 個案層（專案專屬實錄）' },
  ],
  domains: [
    { name: 'WORKFLOW', count: 43, desc: '開發流程與驗證' },
    { name: 'FRONTEND', count: 29, desc: '前端與 UI' },
    { name: 'KNOWLEDGE', count: 22, desc: '知識庫與自我進化' },
    { name: 'WHATSAPP', count: 22, desc: '通訊渠道' },
    { name: 'BUSINESS', count: 20, desc: '商業交付與行銷' },
    { name: 'BACKEND', count: 17, desc: 'API 與資料' },
    { name: 'CF-DEPLOY', count: 14, desc: 'Cloudflare 與部署' },
    { name: 'ENV', count: 11, desc: '環境與編碼' },
    { name: 'SECURITY', count: 9, desc: '安全與認證' },
    { name: 'PROJECT', count: 8, desc: '專案專屬' },
    { name: 'PSYCH', count: 7, desc: '心理學與說服' },
  ],
};

const LAYERS: { tag: string; name: string; desc: string; how: string; tier?: string }[] = [
  { tag: 'L0', name: '導航層', desc: 'rules_map.json — 14 個場景 → 閱讀路徑', how: '唔知讀邊條，先睇地圖' },
  { tag: 'L1', name: '核心層', desc: 'Tier core：跨專案通用（99／103／108／130／150／160／161／168／192／207／213／215）', how: '任何任務都先讀', tier: 'core' },
  { tag: 'L2', name: '領域層', desc: 'Tier domain：11 個分類域', how: '按場景揀', tier: 'domain' },
  { tag: 'L3', name: '個案層', desc: 'Tier case：專案實錄、踩坑個案', how: '遇到先查', tier: 'case' },
];

const GOLD = '#E8FF00';
const INK = '#D4D4D4';
const MUTED = '#8A8A8A';
const BOX = '#1A1A1A';
const LINE = '#404040';

// ---------- 架構圖 ----------
const ArchDiagram: React.FC<{ tools: number; version: string; rules: number }> = ({ tools, version, rules }) => (
  <svg viewBox="0 0 900 400" className="w-full h-auto" role="img" aria-label="MotherBase-KB 架構圖">
    <defs>
      <marker id="mbk-arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
        <path d="M 0 0 L 10 5 L 0 10 z" fill={GOLD} />
      </marker>
    </defs>

    {/* 左：智能體 */}
    {[
      { y: 60, t: 'WorkBuddy', s: '本機 stdio' },
      { y: 130, t: 'ZCode / Kimi', s: '本機 agent' },
      { y: 200, t: '遠端智能體', s: '任何其他平台' },
    ].map(a => (
      <g key={a.t}>
        <rect x="18" y={a.y} width="150" height="54" rx="8" fill={BOX} stroke={LINE} />
        <text x="34" y={a.y + 24} fill={INK} fontSize="14" fontWeight="600">{a.t}</text>
        <text x="34" y={a.y + 42} fill={MUTED} fontSize="11">{a.s}</text>
      </g>
    ))}

    {/* 箭咀：智能體 → 通道 */}
    <path d="M 168 87 L 232 96" stroke={GOLD} strokeWidth="1.6" fill="none" markerEnd="url(#mbk-arrow)" />
    <path d="M 168 157 L 232 130" stroke={GOLD} strokeWidth="1.6" fill="none" markerEnd="url(#mbk-arrow)" />
    <path d="M 168 227 L 232 210" stroke={GOLD} strokeWidth="1.6" fill="none" markerEnd="url(#mbk-arrow)" />

    {/* 中：雙通道 */}
    <rect x="234" y="72" width="180" height="48" rx="8" fill="#0F0F0F" stroke={GOLD} strokeOpacity="0.45" />
    <text x="252" y="93" fill={GOLD} fontSize="13" fontWeight="700">stdio（本機直連）</text>
    <text x="252" y="110" fill={MUTED} fontSize="11">讀到 E:\Projects\motherbase\</text>

    <rect x="234" y="186" width="180" height="48" rx="8" fill="#0F0F0F" stroke={GOLD} strokeOpacity="0.45" />
    <text x="252" y="207" fill={GOLD} fontSize="13" fontWeight="700">HTTPS tunnel（遠端）</text>
    <text x="252" y="224" fill={MUTED} fontSize="11">cloudflared → /mcp + Bearer</text>

    {/* 箭咀：通道 → Server */}
    <path d="M 414 96 L 470 130" stroke={GOLD} strokeWidth="1.6" fill="none" markerEnd="url(#mbk-arrow)" />
    <path d="M 414 210 L 470 166" stroke={GOLD} strokeWidth="1.6" fill="none" markerEnd="url(#mbk-arrow)" />

    {/* 中右：MCP Server */}
    <rect x="472" y="104" width="196" height="86" rx="10" fill={BOX} stroke={GOLD} strokeOpacity="0.6" />
    <text x="492" y="130" fill={INK} fontSize="14" fontWeight="700">MotherBase MCP Server</text>
    <text x="492" y="150" fill={GOLD} fontSize="12" fontFamily="monospace">v{version} · {tools} 個工具</text>
    <text x="492" y="170" fill={MUTED} fontSize="11">read 全開放／write 需授權</text>

    {/* 箭咀：Server → SOULS */}
    <path d="M 668 147 L 716 147" stroke={GOLD} strokeWidth="1.6" fill="none" markerEnd="url(#mbk-arrow)" />

    {/* 右：SOULS 真相源 */}
    <rect x="718" y="88" width="166" height="118" rx="10" fill={BOX} stroke={LINE} />
    <text x="736" y="112" fill={INK} fontSize="14" fontWeight="700">SOULS／真相源</text>
    <text x="736" y="136" fill={GOLD} fontSize="12" fontFamily="monospace">system_rules {rules}</text>
    <text x="736" y="156" fill={MUTED} fontSize="11">knowledge_base</text>
    <text x="736" y="174" fill={MUTED} fontSize="11">failure_patterns</text>
    <text x="736" y="192" fill={MUTED} fontSize="11">rules_map.json</text>

    {/* 底部回流：SOULS → 智能體（閉環） */}
    <path
      d="M 800 206 C 800 330, 620 352, 300 352 C 180 352, 110 330, 92 244"
      stroke={GOLD} strokeWidth="1.8" fill="none" strokeDasharray="7 6" markerEnd="url(#mbk-arrow)"
    />
    <rect x="330" y="316" width="286" height="34" rx="8" fill="#0F0F0F" stroke={GOLD} strokeOpacity="0.35" />
    <text x="348" y="338" fill={GOLD} fontSize="12.5" fontWeight="600">↺ 養料回流 reflow：踩坑 → pitfalls → 守則 → 下一個智能體</text>

    {/* 標題註腳 */}
    <text x="18" y="382" fill={MUTED} fontSize="11">
      兩條通道共用同一份工具實作（http_server.py → from server import mcp），行為永遠一致
    </text>
  </svg>
);

// ---------- 主版面 ----------
const MotherBaseKbTab: React.FC = () => {
  const raw = useMarkdownContent(CONTENT_PATH);

  const snapFromMd: KbSnapshot = useMemo(() => {
    if (!raw) return FALLBACK;
    const m = raw.match(/```json\s*\n([\s\S]*?)```/);
    if (!m) return FALLBACK;
    try {
      const d = JSON.parse(m[1]);
      return {
        as_of: d.as_of ?? FALLBACK.as_of,
        server: { ...FALLBACK.server, ...(d.server || {}) },
        rules: { ...FALLBACK.rules, ...(d.rules || {}) },
        tiers: Array.isArray(d.tiers) && d.tiers.length ? d.tiers : FALLBACK.tiers,
        domains: Array.isArray(d.domains) && d.domains.length ? d.domains : FALLBACK.domains,
      };
    } catch {
      return FALLBACK;
    }
  }, [raw]);

  // P2-6：活數據優先——vault /api/knowhow/stats 即時統計；md 快照退為後備
  const [liveSnap, setLiveSnap] = useState<KbSnapshot | null>(null);
  useEffect(() => {
    let alive = true;
    fetch('/api/knowhow/stats')
      .then(r => (r.ok ? r.json() : null))
      .then((d: any) => {
        if (!alive || !d || !d.success) return;
        const mdT = new Map(snapFromMd.tiers.map(t => [t.name, t.desc]));
        const mdD = new Map(snapFromMd.domains.map(x => [x.name, x.desc]));
        setLiveSnap({
          as_of: `${String(d.generated_at || '').slice(0, 16).replace('T', ' ')}（vault 即時）`,
          server: snapFromMd.server,
          rules: { ...snapFromMd.rules, ...(d.rules || {}) },
          tiers: (d.tiers || []).map((t: any) => ({ name: t.name, count: t.count, desc: mdT.get(t.name) ?? '' })),
          domains: (d.domains || []).map((x: any) => ({ name: x.name, count: x.count, desc: mdD.get(x.name) ?? '' })),
        });
      })
      .catch(() => { /* 後備快照頂住 */ });
    return () => { alive = false; };
  }, [snapFromMd]);

  const snap = liveSnap ?? snapFromMd;

  const prose = useMemo(() => {
    if (!raw) return '';
    return raw
      .replace(/^#\s+[^\n]+\n+/, '')
      .replace(/```json[\s\S]*?```\n?/g, '');
  }, [raw]);

  const maxCount = Math.max(...snap.domains.map(d => d.count), 1);
  const totalRules = snap.domains.reduce((s, d) => s + d.count, 0);

  const StatCard: React.FC<{ icon: React.ReactNode; label: string; value: string | number; sub?: string }> =
    ({ icon, label, value, sub }) => (
      <div className="p-5 bg-jkd-gray-100 rounded-lg border border-gray-800">
        <div className="flex items-center gap-2 mb-2 text-jkd-gold">{icon}
          <span className="text-xs uppercase tracking-wider text-gray-500">{label}</span>
        </div>
        <p className="text-3xl font-bold text-white font-mono">{value}</p>
        {sub && <p className="text-xs text-gray-500 mt-1">{sub}</p>}
      </div>
    );

  const Chip: React.FC<{ ok: boolean; label: string; value: number }> = ({ ok, label, value }) => (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs border ${
      ok ? 'bg-green-950/30 text-green-400 border-green-600/30' : 'bg-amber-950/30 text-amber-400 border-amber-600/30'
    }`}>
      {ok ? <ShieldCheck className="h-3.5 w-3.5" /> : <AlertTriangle className="h-3.5 w-3.5" />}
      {label} <span className="font-mono">{value}</span>
    </span>
  );

  return (
    <div>
      {/* 標題 */}
      <div className="mb-6">
        <h2 className="text-2xl md:text-3xl font-bold text-white mb-2 flex items-center gap-3">
          <Database className="h-8 w-8 text-jkd-gold" /> 母機知識庫 MotherBase-KB
        </h2>
        <p className="text-gray-400">
          {snap.rules.total} 條實證守則嘅外部大腦——智能體接入即承繼，做完嘢再把新坑蒸餾返入庫（讀／寫／回流三職能）
          <span className={`ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs border ${
            liveSnap ? 'bg-green-950/30 text-green-400 border-green-600/30' : 'bg-amber-950/30 text-amber-400 border-amber-600/30'
          }`}>{liveSnap ? '即時數據' : '後備快照'}</span>
          {snap.as_of !== '—' && <span className="text-gray-500"> · 數據 {snap.as_of}</span>}
        </p>
      </div>

      {/* 指標卡 */}
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-4 mb-8">
        <StatCard icon={<Server className="h-4 w-4" />} label="規則總數" value={snap.rules.total}
          sub={`active ${snap.rules.active} · draft ${snap.rules.draft} · 墓碑 ${snap.rules.tombstones}`} />
        <StatCard icon={<Cpu className="h-4 w-4" />} label="MCP 工具" value={snap.server.tools}
          sub={`server v${snap.server.version}`} />
        <StatCard icon={<Radio className="h-4 w-4" />} label="分類域" value={snap.domains.length}
          sub="鐵律：永遠 11 個，唔新增" />
        <StatCard icon={<Activity className="h-4 w-4" />} label="常駐方式" value="SYSTEM"
          sub="schtasks 開機即跑，唔使登入" />
      </div>

      {/* 架構圖 */}
      <div className="mb-8 p-5 bg-jkd-gray-100 rounded-xl border border-gray-800">
        <h3 className="text-lg font-bold text-gray-200 mb-1">系統架構：一條閉環</h3>
        <p className="text-sm text-gray-500 mb-4">
          智能體 →（雙通道）→ MCP Server → SOULS → 養料回流返去智能體
        </p>
        <ArchDiagram tools={snap.server.tools} version={snap.server.version} rules={snap.rules.total} />
      </div>

      {/* 四層架構 */}
      <div className="mb-8">
        <h3 className="text-lg font-bold mb-3 text-gray-200 border-b border-gray-800 pb-2">四層架構（L0 → L3）</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          {LAYERS.map(l => (
            <div key={l.tag} className="p-4 bg-jkd-gray-100 rounded-lg border border-gray-800 hover:border-jkd-gold/30 transition-colors">
              <div className="flex items-center gap-2 mb-2">
                <span className="px-2 py-0.5 bg-black text-jkd-gold text-xs font-mono rounded">{l.tag}</span>
                <span className="font-semibold text-white text-sm">{l.name}</span>
                {l.tier && (
                  <span className="ml-auto font-mono text-xs text-jkd-gold">
                    {snap.tiers.find(t => t.name === l.tier)?.count ?? 0} 條
                  </span>
                )}
              </div>
              <p className="text-xs text-gray-400 mb-2 leading-relaxed">{l.desc}</p>
              <p className="text-xs text-jkd-gold/80">→ {l.how}</p>
            </div>
          ))}
        </div>
      </div>

      {/* 域分佈 */}
      <div className="mb-8">
        <h3 className="text-lg font-bold mb-3 text-gray-200 border-b border-gray-800 pb-2">
          11 個分類域分佈<span className="ml-2 text-sm font-normal text-gray-500">共 {totalRules} 條</span>
        </h3>
        <div className="space-y-2">
          {snap.domains.map(d => (
            <div key={d.name} className="flex items-center gap-3">
              <span className="w-24 shrink-0 font-mono text-xs text-jkd-gold">{d.name}</span>
              <div className="flex-1 h-5 bg-jkd-gray-100 rounded overflow-hidden border border-gray-800">
                <div
                  className="h-full bg-jkd-gold/70"
                  style={{ width: `${Math.round((d.count / maxCount) * 100)}%` }}
                />
              </div>
              <span className="w-10 shrink-0 text-right font-mono text-xs text-white">{d.count}</span>
              <span className="hidden md:block w-40 shrink-0 text-xs text-gray-500 truncate">{d.desc}</span>
            </div>
          ))}
        </div>
      </div>

      {/* 體檢 */}
      <div className="mb-8 p-5 bg-jkd-gray-100 rounded-xl border border-gray-800">
        <h3 className="text-lg font-bold text-gray-200 mb-3 flex items-center gap-2">
          <Activity className="h-4 w-4 text-jkd-gold" /> 體檢 <span className="font-mono text-sm text-gray-500">rule_health()</span>
        </h3>
        <div className="flex flex-wrap gap-2">
          <Chip ok={snap.rules.collisions === 0} label="重號對 collisions" value={snap.rules.collisions} />
          <Chip ok={snap.rules.unknown_categories === 0} label="越界域 unknown_categories" value={snap.rules.unknown_categories} />
          <Chip ok={snap.rules.stale_tombstones === 0} label="陳舊墓碑 stale_tombstones" value={snap.rules.stale_tombstones} />
          <Chip ok={snap.rules.missing_frontmatter === 0} label="缺 frontmatter" value={snap.rules.missing_frontmatter} />
          <Chip ok={snap.rules.missing_tags === 0} label="缺 tags" value={snap.rules.missing_tags} />
          <Chip ok={snap.rules.tombstones === 0} label="墓碑 tombstones" value={snap.rules.tombstones} />
        </div>
        <p className="text-xs text-gray-500 mt-3">
          目標全部為 0（2026-09-28 治理後全綠）。draft＝自動沉澱、未經 Captain 逐字審閱，審完先升 active。
        </p>
      </div>

      {/* 遠端通道 */}
      <div className="mb-8 p-5 rounded-xl border border-jkd-gold/25 bg-jkd-gold/5 flex items-start gap-3">
        <Radio className="h-5 w-5 text-jkd-gold shrink-0 mt-0.5" />
        <div>
          <p className="font-semibold text-jkd-gold mb-1">遠端通道（其他機器／其他平台）</p>
          <p className="text-sm text-gray-300 font-mono break-all">{snap.server.http}</p>
          <p className="text-xs text-gray-500 mt-1">
            每次工具呼叫＝一次 HTTPS 請求（唔係長連接）；驗證：<code className="text-gray-400">python scripts/http_smoke_test.py --deep</code>
          </p>
        </div>
      </div>

      {/* 詳細內容 */}
      {prose && (
        <div className="mt-10">
          <div className="flex items-center gap-2 mb-4 text-gray-300">
            <ArrowRight className="h-4 w-4 text-jkd-gold" />
            <h3 className="text-lg font-bold">完整說明</h3>
          </div>
          <article className="knowhow-prose prose prose-invert max-w-none text-gray-300">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{prose}</ReactMarkdown>
          </article>
        </div>
      )}

      <div className="mt-8 flex items-center gap-2 text-xs text-gray-600">
        <RefreshCw className="h-3.5 w-3.5" /> 上圖數據來自 <code className="text-gray-500">/api/knowhow/stats</code>（vault 即時統計，同步鏈部署後自動正確）；md JSON 快照已退役為離線後備（P2-6，2026-10-24）
      </div>
    </div>
  );
};

export default MotherBaseKbTab;
